import { Box, Container, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import { Waypoint } from 'react-waypoint';

import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import NullStateDiv from '@components/NullStateDiv';
import TimelineLoader from '@components/TimelineLoader';
import TimelineTree from '@components/TimelineTree/index';

import { SCAN_BATCH_SIZE } from '@constants/scan';

import { useIsMobile } from '@hooks/useIsMobile';
import { useScanResults } from '@hooks/useScanResults';

import type { ObservationRecord } from '@models/observation';
import type { ScanResult } from '@models/scan';

import type { ScanResultModel } from 'generated/types';

import ScanCard from './Common/ScanCard';
import EmptyState from './EmptyState';

const messages = defineMessages({
  noData: {
    id: 'scanHistoryMain.noData',
    defaultMessage: 'No {type} Scans',
  },
  allShown: {
    id: 'scanHistoryMain.allShown',
    defaultMessage: 'All {type} Scans Shown',
  },
});

interface ScanHistoryContentProps {
  /** Scan results */
  data: (Omit<ScanResultModel, 'observations'> & {
    observations?: ObservationRecord[];
  })[];
  /** Is data loading? */
  loading: boolean;
  /** Is there more data to be fetched? */
  hasMore?: boolean;
  /** Callback that fetches more scan results if there are still more */
  onEnter: () => Promise<void>;
  /** Are the scan results being shared with an external user? */
  isSharing?: boolean;
  /** FHIR patient ID */
  patientId?: string | null;
}

const ScanHistoryContent = ({
  data,
  hasMore = false,
  isSharing = false,
  loading,
  onEnter: handleEnter,
  patientId,
}: ScanHistoryContentProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const { friendly_type, scan_type } = useScanResults();

  const sortedScans = data.reduce<Record<string, ScanResult[]>>((acc, curr) => {
    const year = dayjs(curr.date).format('YYYY');
    if (curr.scan_type !== scan_type) {
      return acc;
    }
    if (!(year in acc)) {
      acc[year] = [curr];
    } else {
      acc[year].push(curr);
    }
    return acc;
  }, {});

  const scanResults = Object.keys(sortedScans)
    .sort((a, b) => Number(b) - Number(a))
    .map((year, yearIndex) => {
      return (
        <Box
          sx={{ display: 'flex' }}
          key={`scan-history-timeline-${yearIndex}`}
        >
          <TimelineTree
            header={loading ? undefined : year}
            nodes={sortedScans[year].map((d) => (
              <ScanCard
                data={d}
                key={`${scan_type}-scan-history-card-${d.result_id}`}
                loading={loading}
                patientId={patientId}
              />
            ))}
          />
        </Box>
      );
    });

  const hasData = Boolean(data.length);

  return (
    <Container maxWidth="lg" disableGutters>
      {hasData && loading && scanResults}
      <TimelineLoader
        count={data.length >= SCAN_BATCH_SIZE ? 2 : isMobile ? 4 : 8}
        loading={loading}
      >
        {hasData ? (
          <>
            {scanResults}
            {!hasMore && (
              <Typography py={3} textAlign="center" variant="subtitle1">
                {formatMessage(messages.allShown, {
                  type: friendly_type,
                })}
              </Typography>
            )}
          </>
        ) : isSharing ? (
          <NullStateDiv>
            {formatMessage(messages.noData, {
              type: friendly_type,
            })}
          </NullStateDiv>
        ) : (
          <EmptyState />
        )}
      </TimelineLoader>

      <Waypoint onEnter={handleEnter} />
    </Container>
  );
};

export default withErrorBoundary(ScanHistoryContent, {
  FallbackComponent: ErrorBoundaryFallback,
});
